import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { getEncryptionNonce } from '@/core/web-storage/webStorage.utils';

const algorithm = 'AES-CTR';

async function getCryptoKey() {
  const base64KeyString = ENCRYPTION_KEY; //base64 8bit key
  const keyBuffer = getDataEncoding(base64KeyString);
  const cryptoKey = await window.crypto.subtle.importKey('raw', keyBuffer, algorithm, false, [
    'encrypt',
    'decrypt',
  ]);
  return cryptoKey;
}

export function getDataEncoding(data: string) {
  const encoder = new TextEncoder();
  return encoder.encode(data);
}

export function getDataDecoding(data: ArrayBuffer) {
  const decoder = new TextDecoder();
  return decoder.decode(data);
}

export function stringToBase64(string: string) {
  return btoa(string);
}

export function base64ToString(base64String: string) {
  return atob(base64String);
}

export function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function base64ToArrayBuffer(base64: string) {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

async function encrypt(data: string) {
  const key = await getCryptoKey();
  const counter = window.crypto.getRandomValues(new Uint8Array(16));
  const counterBase64 = arrayBufferToBase64(counter);
  webStorage.local.setItem(LOCAL_STORAGE_KEYS.ENCRYPTION_NONCE, counterBase64);
  const encodedData = getDataEncoding(data);
  const encryptedData = await window.crypto.subtle.encrypt(
    {
      name: algorithm,
      counter: counter,
      length: 64,
    },
    key,
    encodedData,
  );
  return encryptedData;
}

async function decrypt(encryptedData: BufferSource) {
  const key = await getCryptoKey();
  const counterOutBase64 = getEncryptionNonce();
  const counter = base64ToArrayBuffer(counterOutBase64);
  const decryptedData = await window.crypto.subtle.decrypt(
    {
      name: algorithm,
      counter: counter,
      length: 64,
    },
    key,
    encryptedData,
  );
  const decodedData = getDataDecoding(decryptedData);
  webStorage.local.removeItem(LOCAL_STORAGE_KEYS.ENCRYPTION_NONCE);
  return decodedData;
}

function xorEncryptDecrypt(data?: string) {
  if (data && ENCRYPTION_KEY.length >= data.length) {
    const dataArray = data.split('');
    for (let i = 0; i < dataArray.length; i++) {
      dataArray[i] = String.fromCharCode(dataArray[i].charCodeAt(0) ^ ENCRYPTION_KEY.charCodeAt(i));
    }
    return dataArray.join('');
  }
  return '';
}

export { encrypt, decrypt, xorEncryptDecrypt };
